"use client";

import { useState, useMemo } from "react";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { motion } from "framer-motion";
import { BlogCard, type BlogPost } from "./blog-card";

export function BlogGrid({
  posts,
  locale,
  featuredPost,
}: {
  posts: BlogPost[];
  locale: string;
  featuredPost: BlogPost;
}) {
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(posts.map((p) => p.category).filter(Boolean))
    ) as string[];
    return [{ key: "all", label: locale === "ar" ? "الكل" : "All" }].concat(
      unique.map((c) => ({ key: c, label: c }))
    );
  }, [posts, locale]);

  const filteredPosts =
    activeCategory === "all"
      ? posts
      : posts.filter((p) => p.category === activeCategory);

  const featuredTitle =
    locale === "ar"
      ? featuredPost.title_ar || featuredPost.title_en
      : featuredPost.title_en;
  const featuredExcerpt =
    locale === "ar"
      ? featuredPost.excerpt_ar || featuredPost.excerpt_en
      : featuredPost.excerpt_en;
  const featuredDate = featuredPost.published_at
    ? new Date(featuredPost.published_at).toLocaleDateString(
        locale === "ar" ? "ar-SA" : "en-US",
        { year: "numeric", month: "long", day: "numeric" }
      )
    : null;

  return (
    <div>
      {/* Featured post hero */}
      <section className="relative overflow-hidden rounded-2xl border border-(--fm-border) bg-(--fm-surface) mb-12">
        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[360px]">
            {featuredPost.cover_image_url ? (
              <Image
                src={featuredPost.cover_image_url}
                alt={featuredTitle}
                fill
                className="object-cover"
                priority
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-(--fm-accent)/20 to-(--fm-accent)/5" />
            )}
          </div>

          {/* Content */}
          <div className="p-8 md:p-10 flex flex-col justify-center">
            {featuredPost.category && (
              <span className="inline-block w-fit px-3 py-1 text-xs font-medium rounded-full bg-(--fm-accent)/10 text-(--fm-accent) mb-4">
                {featuredPost.category}
              </span>
            )}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-(--fm-text-primary) mb-3">
              {featuredTitle}
            </h2>
            {featuredDate && (
              <p className="text-sm text-(--fm-text-secondary) mb-3">
                {featuredDate}
              </p>
            )}
            {featuredExcerpt && (
              <p className="text-(--fm-text-secondary) leading-relaxed mb-6 line-clamp-3">
                {featuredExcerpt}
              </p>
            )}
            <Link
              href={`/blog/${featuredPost.slug}`}
              className="inline-flex items-center gap-1 text-(--fm-accent) font-medium hover:underline"
            >
              {locale === "ar" ? "اقرأ المقال ←" : "Read Article →"}
            </Link>
          </div>
        </div>
      </section>

      {/* Category filter tabs */}
      {categories.length > 1 && (
        <div className="flex items-center gap-2 mb-8 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                activeCategory === cat.key
                  ? "bg-(--fm-accent) text-(--fm-background)"
                  : "bg-(--fm-surface) text-(--fm-text-secondary) border border-(--fm-border) hover:border-(--fm-accent)/40"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.map((post, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
          >
            <BlogCard post={post} locale={locale} />
          </motion.div>
        ))}
      </div>

      {filteredPosts.length === 0 && (
        <div className="text-center py-16">
          <p className="text-(--fm-text-secondary)">
            {locale === "ar"
              ? "لا توجد مقالات في هذا التصنيف."
              : "No posts in this category."}
          </p>
        </div>
      )}
    </div>
  );
}
